import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Paper, Typography } from '@mui/material';
//
import Image from '../image/Image';
import RejectionFiles from './RejectionFiles';
import BlockContent from './BlockContent';
import Iconify from '../iconify/Iconify';

// ----------------------------------------------------------------------

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
}));

// ----------------------------------------------------------------------

// Helper function to get file extension
const getFileExtension = (fileName) => fileName.split('.').pop()?.toLowerCase();

// Helper function to check if file is image
const isImageFile = (file) => {
  if (typeof file === 'string') return /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
  return file?.type?.startsWith('image/');
};

function FileNamePreview({ file }) {
  const fileName = typeof file === 'string' ? file : file?.name || 'Unknown file';
  const fileExtension = getFileExtension(fileName);

  // Map file extensions to icons
  const getFileIcon = (extension) => {
    switch (extension) {
      case 'pdf':
        return 'mdi:file-pdf-box';
      case 'csv':
        return 'mdi:file-delimited-outline';
      case 'xlsx':
      case 'xls':
        return 'mdi:file-excel';
      case 'doc':
      case 'docx':
        return 'mdi:file-word';
      case 'txt':
      case 'md':
        return 'mdi:file-document-outline';
      default:
        return 'mdi:file-document';
    }
  };

  return (
    <Paper
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        width: 'calc(100% - 16px)',
        height: 'calc(100% - 16px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 2,
        bgcolor: 'grey.50',
      }}
    >
      <Iconify icon={getFileIcon(fileExtension)} width={48} height={48} color="text.secondary" />
      <Typography variant="body2" align="center" sx={{ wordBreak: 'break-all' }}>
        {fileName}
      </Typography>
      {fileExtension && (
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
          {fileExtension} file
        </Typography>
      )}
    </Paper>
  );
}

// File Name Preview Component
FileNamePreview.propTypes = {
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
};

export default function UploadSingleFile({ error = false, file, helperText, sx, ...other }) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    ...other,
  });

  const renderFilePreview = () => {
    if (!file) return null;

    if (isImageFile(file)) {
      return (
        <Image
          alt="file preview"
          src={typeof file === 'string' ? file : file.preview}
          sx={{
            top: 8,
            left: 8,
            borderRadius: 1,
            position: 'absolute',
            width: 'calc(100% - 16px)',
            height: 'calc(100% - 16px)',
          }}
        />
      );
    }

    // For all non-image files (including PDFs), show filename with icon
    return <FileNamePreview file={file} />;
  };

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
          ...(file && {
            padding: '12% 0',
          }),
        }}
      >
        <input {...getInputProps()} />

        <BlockContent />

        {renderFilePreview()}
      </DropZoneStyle>

      {fileRejections.length > 0 && <RejectionFiles fileRejections={fileRejections} />}

      {helperText && helperText}
    </Box>
  );
}

UploadSingleFile.propTypes = {
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.node,
  sx: PropTypes.object,
};
